import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  ModalDialog,
  ActionRow,
  useToggle,
  Spinner,
} from '@openedx/paragon';
import { getSavingStatuses } from '../../../data/selectors';
import { refreshAdminPortalCache } from '../../../data/thunks';

interface AdminPortalCachedDataProps {
  companyName?: string | null;
  companyFriendlyName?: string | null;
}

const AdminPortalCachedData: React.FC<AdminPortalCachedDataProps> = ({
  companyName = null,
  companyFriendlyName = null,
}) => {
  const dispatch = useDispatch();
  const savingStates = useSelector(getSavingStatuses);

  const [isConfirmModalOpen, openConfirmModal, closeConfirmModal] = useToggle(false);
  const [refreshTarget, setRefreshTarget] = useState<string | null>(null);
  const [refreshMessage, setRefreshMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const handleRefreshClick = (target: string) => {
    setRefreshTarget(target);
    setRefreshMessage(null);
    openConfirmModal();
  };

  const handleConfirmRefresh = async () => {
    if (!refreshTarget) { return; }

    try {
      await dispatch(refreshAdminPortalCache(refreshTarget));
      const targetName = refreshTarget === '*' ? 'all organizations' : refreshTarget;
      setRefreshMessage({
        type: 'success',
        text: `Successfully triggered cache refresh for ${targetName}.`,
      });
      closeConfirmModal();

      // Auto-clear success message after 5 seconds
      setTimeout(() => setRefreshMessage(null), 5000);
    } catch (error) {
      const targetName = refreshTarget === '*' ? 'all organizations' : refreshTarget;
      setRefreshMessage({
        type: 'error',
        text: `Failed to trigger cache refresh for ${targetName}. Please try again.`,
      });
      closeConfirmModal();
    }
  };

  const isRefreshing = savingStates.refreshAdminPortalCacheSavingStatus === 'pending';

  return (
    <>
      <div className="admin-portal-cache-section">
        {companyName && <h5 className="mb-3">Admin Portal Cached Data</h5>}
        <p className="text-muted small mb-3">
          Refresh cached data for the admin portal to ensure all organization data is up to date.
        </p>

        {refreshMessage && (
          <Alert
            variant={refreshMessage.type === 'success' ? 'success' : 'danger'}
            dismissible
            onClose={() => setRefreshMessage(null)}
            className="mb-3"
          >
            {refreshMessage.text}
          </Alert>
        )}

        <div className="cache-refresh-buttons">
          {companyName && (
            <Button
              variant="outline-primary"
              onClick={() => handleRefreshClick(companyName)}
              disabled={isRefreshing}
            >
              {isRefreshing && refreshTarget === companyName ? (
                <>
                  <Spinner animation="border" size="sm" className="mr-2" />
                  Refreshing...
                </>
              ) : (
                `Refresh Cached Data for ${companyFriendlyName || companyName}`
              )}
            </Button>
          )}
          <Button
            variant="outline-secondary"
            onClick={() => handleRefreshClick('*')}
            disabled={isRefreshing}
          >
            {isRefreshing && refreshTarget === '*' ? (
              <>
                <Spinner animation="border" size="sm" className="mr-2" />
                Refreshing...
              </>
            ) : (
              'Refresh Cached Data for All Orgs'
            )}
          </Button>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ModalDialog
        title="Confirm Cache Refresh"
        isOpen={isConfirmModalOpen}
        onClose={closeConfirmModal}
        hasCloseButton
      >
        <ModalDialog.Header>
          <ModalDialog.Title>Confirm Cache Refresh</ModalDialog.Title>
        </ModalDialog.Header>
        <ModalDialog.Body>
          <p>
            Are you sure you want to refresh the cached data for{' '}
            <strong>
              {refreshTarget === '*' ? 'all organizations' : refreshTarget}
            </strong>?
          </p>
        </ModalDialog.Body>
        <ModalDialog.Footer>
          <ActionRow>
            <ModalDialog.CloseButton variant="tertiary">
              Cancel
            </ModalDialog.CloseButton>
            <Button
              variant="primary"
              onClick={handleConfirmRefresh}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <>
                  <Spinner animation="border" size="sm" className="mr-2" />
                  Refreshing...
                </>
              ) : (
                'Confirm'
              )}
            </Button>
          </ActionRow>
        </ModalDialog.Footer>
      </ModalDialog>
    </>
  );
};

export default AdminPortalCachedData;
